import {
  EmbindPtr,
  EmbindVector,
  PRMarinaAppearance,
  PRMarinaBlur,
  PRMarinaBorder,
  PRMarinaBorderOptions,
  PRMarinaColor,
  PRMarinaFill,
  PRMarinaGradient,
  PRMarinaLayerVerticalTextAlignmentValue,
  PRMarinaShadowStyle,
} from '@sketch-hq/sketch-web-renderer'
import {
  Appearance,
  Blur,
  Border,
  BorderOptions,
  Color,
  Fill,
  Gradient,
  GradientStops,
  Shadow,
} from '../../types'
import _pick from 'lodash/pick'
import {
  prBlendModeEnumToBlendModeMap,
  prBlurEnumTypeToBlurTypeMap,
  prBorderLineCapStyleEnumToBorderLineCapStyleMap,
  prBorderLineJoinStyleEnumToBorderLineJoinStyleMap,
  prBorderPositionEnumToBorderPositionMap,
  prFillEnumTypeToFillTypeMap,
  prGradientTypeEnumToGradientTypeMap,
  prVerticalTextAlignmentEnumToVerticalTextAlignmentMap,
} from './presentationMarinaEnumsMapping'
import { VerticalTextAlignment } from 'modules/shares/types'

/**
 * @file
 * The types coming from the web-renderer are most of the time not usable directly in the frontend.
 * Some property name can vary, arrays are EmbindVectors, object contain Embind object properties, etc.
 * This file contains functions to transform the types coming from the web-renderer to the types we use
 * in SketchScene.
 */

/**
 * Transform a color in the web-renderer presentation format
 * to a SketchScene color.
 */
export function transformPresentationFileColor(
  prMarinaColor: PRMarinaColor
): Color {
  const colorValue = prMarinaColor.value
  const colorVar = prMarinaColor.getVariableReference()
  return {
    blue: colorValue.b,
    green: colorValue.g,
    red: colorValue.r,
    alpha: colorValue.a,
    colorVariableID: colorVar?.id,
    colorVariableName: colorVar?.name,
    colorVariableRemoteID: colorVar?.remoteId,
    colorVariableSourceLibraryID: colorVar?.sourceLibraryId,
    colorVariableSourceLibraryName: colorVar?.sourceLibraryName,
  }
}

/**
 * Remove embind object properties and map enum to expected value
 */
export function transformPresentationBlur(
  prBlur: EmbindPtr & PRMarinaBlur
): Blur {
  const { type: prBlurType } = prBlur
  const typeEnumName = prBlurType.constructor.name
  const type = prBlurEnumTypeToBlurTypeMap[typeEnumName]

  if (!type) {
    throw new Error(`Unknown blur type: ${typeEnumName}`)
  }
  // Create new object without EmbindPtr
  return {
    type,
    ..._pick(prBlur, [
      'isEnabled',
      'center',
      'motionAngle',
      'radius',
      'saturation',
    ]),
  }
}

export function transformPresentationFills(
  prFills: EmbindVector<PRMarinaFill>
): Fill[] {
  const fills: Fill[] = []

  for (let i = 0; i < prFills.size(); i++) {
    const prFill = prFills.get(i)
    if (prFill) {
      const prFillType = prFill.fillType.constructor.name
      const fillType = prFillEnumTypeToFillTypeMap[prFillType]
      const prColor = prFill.getColor()
      const appearance = prFill.appearance

      if (!fillType) {
        throw new Error(`Unknown fill type: ${prFillType}`)
      }

      fills.push({
        isEnabled: prFill.isEnabled,
        type: fillType,
        color: prColor ? transformPresentationFileColor(prColor) : undefined,
        appearance: transformPresentationAppearance(appearance),
      })
    }
  }

  return fills
}

export function transformPresentationAppearance(
  prAppearance: PRMarinaAppearance
): Appearance {
  const prBlendMode = prAppearance.blendMode.constructor.name
  const blendMode = prBlendModeEnumToBlendModeMap[prBlendMode]
  if (!blendMode) {
    throw new Error(`Unknown blend mode: ${prBlendMode}`)
  }

  return {
    blendMode: blendMode,
    opacity: prAppearance.opacity,
  }
}

export function transformPresentationBorders(
  prBorders: EmbindVector<PRMarinaBorder>
) {
  const borders: Border[] = []

  for (let i = 0; i < prBorders.size(); i++) {
    const prBorder = prBorders.get(i)

    if (prBorder) {
      const prBorderPosition = prBorder.position.constructor.name
      const borderPosition =
        prBorderPositionEnumToBorderPositionMap[prBorderPosition]

      /**
       * @todo
       * Fix this once the fix for ".getGradient due to unbound types" error is available in the web-renderer
       */
      const prGradient = undefined // prBorder.getGradient()
      const prBorderFillType = prBorder.fillType.constructor.name
      const borderFillType = prFillEnumTypeToFillTypeMap[prBorderFillType]
      if (!borderFillType) {
        throw new Error(`Unknown border fill type ${prBorderFillType}`)
      }

      const borderColor = prBorder.getColor()

      borders.push({
        appearance: transformPresentationAppearance(prBorder.appearance),
        isEnabled: prBorder.isEnabled,
        thickness: prBorder.thickness,
        position: borderPosition,
        color: borderColor
          ? transformPresentationFileColor(borderColor)
          : undefined,
        gradient: prGradient
          ? transformPresentationGradient(prGradient)
          : undefined,
        fillType: borderFillType,
      })
    }
  }

  return borders
}

function transformPresentationGradient(
  prGradient: EmbindPtr & PRMarinaGradient
): Gradient {
  const gradientType =
    prGradientTypeEnumToGradientTypeMap[prGradient.type.constructor.name]

  const prGradientStops = prGradient.stops
  const gradientStops: GradientStops[] = []

  for (let i = 0; i < prGradientStops.size(); i++) {
    const prGradientStop = prGradientStops.get(i)
    if (prGradientStop) {
      gradientStops.push({
        color: transformPresentationFileColor(prGradientStop.color),
        position: prGradientStop.position,
      })
    }
  }

  return {
    from: prGradient.from,
    to: prGradient.to,
    type: gradientType,
    stops: gradientStops,
  }
}

export function transformPresentationBorderOptions(
  prBorderOptions: PRMarinaBorderOptions
): BorderOptions {
  const lineCapStyle =
    prBorderLineCapStyleEnumToBorderLineCapStyleMap[
      prBorderOptions.lineCapStyle.constructor.name
    ]
  if (!lineCapStyle) {
    throw new Error(`Unknown line cap style: ${prBorderOptions.lineCapStyle}`)
  }

  const lineJoinStyle =
    prBorderLineJoinStyleEnumToBorderLineJoinStyleMap[
      prBorderOptions.lineJoinStyle.constructor.name
    ]
  if (!lineJoinStyle) {
    throw new Error(`Unknown line join style: ${prBorderOptions.lineJoinStyle}`)
  }

  return {
    dashPattern: prBorderOptions.dashPattern,
    lineCapStyle: lineCapStyle,
    lineJoinStyle: lineJoinStyle,
  }
}

export function transformPresentationShadows(
  prShadows: EmbindVector<PRMarinaShadowStyle>
): Shadow[] {
  const shadows: Shadow[] = []

  for (let i = 0; i < prShadows.size(); i++) {
    const prShadow = prShadows.get(i)
    if (prShadow) {
      shadows.push({
        isEnabled: prShadow.isEnabled,
        blurRadius: prShadow.radius,
        color: transformPresentationFileColor(prShadow.color),
        offsetX: prShadow.offset.x,
        offsetY: prShadow.offset.y,
        spread: prShadow.spread,
        appearance: transformPresentationAppearance(prShadow.appearance),
      })
    }
  }

  return shadows
}

export function transformPresentationTextVerticalAlignment(
  prVerticalTextAlignment: PRMarinaLayerVerticalTextAlignmentValue
): VerticalTextAlignment {
  const verticalTextAlignment =
    prVerticalTextAlignmentEnumToVerticalTextAlignmentMap[
      prVerticalTextAlignment.constructor.name
    ]

  if (!verticalTextAlignment) {
    throw new Error(
      `Unknown vertical text alignment: ${prVerticalTextAlignment}`
    )
  }

  return verticalTextAlignment
}
